import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { playShutter } from '../../utils/shutterSound';
import { pickEmberColor, glowSprite } from '../../utils/emberPalette';

// Site-wide camera-shaped cursor (front view DSLR) with flash + shutter click,
// leaving a subtle trail of drifting embers as it moves.
const CameraCursor = () => {
  const ref = useRef(null);
  const trailRef = useRef(null);
  const { pathname } = useLocation();
  const active = !pathname.startsWith('/admin') && window.matchMedia('(pointer: fine)').matches;

  useEffect(() => {
    if (!active) return;
    const el = ref.current;
    const canvas = trailRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = window.innerWidth;
    let h = window.innerHeight;
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    document.body.classList.add('xp-cam-active');

    const embers = [];
    let lastSpawn = 0;

    const move = (e) => {
      el.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      el.classList.add('visible');
      el.classList.toggle(
        'over-clickable',
        !!e.target.closest('button, a, input, textarea, select, [role="button"]')
      );
      const now = performance.now();
      if (now - lastSpawn > 34 && embers.length < 40) {
        lastSpawn = now;
        embers.push({
          x: e.clientX + (Math.random() - 0.5) * 6,
          y: e.clientY + (Math.random() - 0.5) * 6,
          vx: (Math.random() - 0.5) * 0.5,
          vy: -(0.3 + Math.random() * 0.6),
          life: 0,
          maxLife: 32 + Math.random() * 24,
          r: 0.5 + Math.random() * 1.2,
          tw: Math.random() * Math.PI * 2,
          col: pickEmberColor(),
        });
      }
    };
    const down = () => {
      el.classList.remove('flashing');
      void el.offsetWidth; // restart animation
      el.classList.add('flashing');
      if (localStorage.getItem('xp-muted') !== '1') playShutter();
    };
    const leave = () => el.classList.remove('visible');

    let raf;
    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      for (let i = embers.length - 1; i >= 0; i--) {
        const p = embers[i];
        p.tw += 0.15;
        p.x += p.vx + Math.sin(p.tw) * 0.3;
        p.y += p.vy;
        p.life += 1;
        if (p.life > p.maxLife) {
          embers.splice(i, 1);
          continue;
        }
        const a = (1 - p.life / p.maxLife) * 0.45;
        const s = p.r * 9;
        ctx.globalAlpha = a;
        ctx.drawImage(glowSprite(p.col), p.x - s / 2, p.y - s / 2, s, s);
        ctx.globalAlpha = 1;
      }
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(tick);
    };
    tick();

    document.addEventListener('mousemove', move);
    document.addEventListener('mousedown', down);
    document.documentElement.addEventListener('mouseleave', leave);
    return () => {
      document.body.classList.remove('xp-cam-active');
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mousedown', down);
      document.documentElement.removeEventListener('mouseleave', leave);
    };
  }, [active]);

  if (!active) return null;
  return (
    <>
      <canvas ref={trailRef} className="xp-cursor-trail" aria-hidden="true" data-testid="cursor-ember-trail" />
      <div ref={ref} className="xp-camera-cursor" aria-hidden="true" data-testid="camera-cursor">
        <div className="xp-camera-body">
          <span className="xp-cam-flash"></span>
          <img src="/experience/camera_cursor.png" alt="" draggable={false} />
        </div>
      </div>
    </>
  );
};

export default CameraCursor;
